import { Card, CardContent, Typography, Box, useTheme, styled, useMediaQuery, Button, CardHeader, Chip, IconButton } from "@mui/material"
import { estimateMathScore, estimateReadingScore } from "./sat-score-estimation"
import { UserContext } from "../UserAndProfile/UserContext"
import { useContext, useMemo, useState, useCallback } from "react"
import SettingsIcon from '@mui/icons-material/Settings';
import CloseIcon from '@mui/icons-material/Close';
import { EditProfile } from "./EditProfile";
import { Link } from "react-router-dom";

import { CollegeScoreSearch} from "./CollegeScoreSearch";

// This would come from your API/database

interface StudentScoreData {
  math: {
    current: number | null;
    starting: number | null;
    target: number | null;
    percentiles: Record<number, number>;
  };
  reading: {
    current: number | null;
    starting: number | null;
    target: number | null;
    percentiles: Record<number, number>;
  };
}

export interface CollegeSearchResult {
  name: string;
  mathScores: {
    '25th': number | null;
    '50th': number | null;
    '75th': number | null;
  };
  readingWritingScores: {
    '25th': number | null;
    '50th': number | null;
    '75th': number | null;
  };
}

export interface ActivePercentiles {
  '25th': boolean;
  '50th': boolean;
  '75th': boolean;
}

interface ScoreBarProps {
  label: string;
  score: number | null;
  startingScore: number | null;
  targetScore: number | null;
  percentiles: Record<number, number>;
  universities: Array<{
    name: string;
    logo: string;
    score: number;
    type: string;
    isTop: boolean;
  }>;
  type: string;
  selectedColleges: CollegeSearchResult[];
  activePercentiles: ActivePercentiles;
}

function useStudentScores(): StudentScoreData {
  const { user } = useContext(UserContext);
  
  // Helper function to get the current score to display in the progress bar
  // Score progression priority:
  // 1. Previous SAT scores (from user profile)
  // 2. Diagnostic test score (when completed)
  // 3. Most recent full-length test score
  // 4. Fallback: null (empty progress bar)
  const getCurrentDisplayScore = (type: 'math' | 'reading') => {
    if (!user) return null;
    
    // 1. Third priority: Most recent completed full-length test
    const completedFullTests = user.testAttempts?.filter(attempt => 
      attempt.testType === 'full' && attempt.completed
    ).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    
    if (completedFullTests && completedFullTests.length > 0) {
      const mostRecentTest = completedFullTests[0];
      if (type === 'math' && mostRecentTest.mathScore) {
        return mostRecentTest.mathScore;
      }
      if (type === 'reading' && mostRecentTest.readingWritingScore) {
        return mostRecentTest.readingWritingScore;
      }
    }
    
    // 2. Second priority: Completed diagnostic test score
    const completedDiagnostic = user.testAttempts?.find(attempt => 
      attempt.testType === 'diagnostic' && attempt.completed
    );
    if (completedDiagnostic) {
      if (type === 'math' && completedDiagnostic.mathScore) {
        return completedDiagnostic.mathScore;
      }
      if (type === 'reading' && completedDiagnostic.readingWritingScore) {
        return completedDiagnostic.readingWritingScore;
      }
    }

    // 3. Third priority: Previous SAT scores from user profile
    if (type === 'math' && user.previousSATScores?.math) {
      const prevScore = parseInt(user.previousSATScores.math);
      if (!isNaN(prevScore) && prevScore >= 200 && prevScore <= 800) {
        return prevScore;
      }
    }
    if (type === 'reading' && user.previousSATScores?.readingWriting) {
      const prevScore = parseInt(user.previousSATScores.readingWriting);
      if (!isNaN(prevScore) && prevScore >= 200 && prevScore <= 800) {
        return prevScore;
      }
    }
    
    
    
    // 4. Fallback: No test scores available, return null
    return null;
  };
  
  // Cache the current display scores
  const currentMathScore = useMemo(() => getCurrentDisplayScore('math'), [user?.testAttempts, user?.previousSATScores]);
  const currentReadingScore = useMemo(() => getCurrentDisplayScore('reading'), [user?.testAttempts, user?.previousSATScores]);
  
  // Cache expensive calculations for estimated scores (keeping for reference/backup)
  const estimatedMathScore = useMemo(() => 
    estimateMathScore(user?.skills).higher,
    [user?.skills]
  );
  const estimatedReadingWritingScore = useMemo(() => 
    estimateReadingScore(user?.skills).higher,
    [user?.skills]
  );

  // Final data structure with conditional logic inside useMemo
  return useMemo(() => {
    if (!user) {
      return {
        math: {
          current: null,
          starting: null,
          target: null,
          percentiles: {
            25: 520,
            50: 600,
            75: 680,
            90: 740,
          },
        },
        reading: {
          current: null,
          starting: null,
          target: null,
          percentiles: {
            25: 540,
            50: 620,
            75: 700,
            90: 760,
          },
        },
      };
    }

    return {
      math: {
        current: currentMathScore, // Use actual test scores instead of estimated
        starting: user?.previousSATScores?.math ? parseInt(user.previousSATScores.math) : null,
        target: user?.targetScores?.math ? parseInt(user.targetScores.math) : null,
        percentiles: {
          25: 520,
          50: 600,
          75: 680,
          90: 740,
        },
      },
      reading: {
        current: currentReadingScore, // Use actual test scores instead of estimated
        starting: user?.previousSATScores?.readingWriting ? parseInt(user.previousSATScores.readingWriting) : null,
        target: user?.targetScores?.readingWriting ? parseInt(user.targetScores.readingWriting) : null,
        percentiles: {
          25: 540,
          50: 620,
          75: 700,
          90: 760,
        },
      }
    };
  }, [user, currentMathScore, currentReadingScore]);
}

const MAX_SCORE = 800
const MIN_SCORE = 200

// Expanded university data with a wider range of colleges
const universities = [
  { name: "Harvard", logo: "/images/collegelogos/harvard.webp", score: 770, type: "math", isTop: false },
  { name: "Stanford", logo: "/images/collegelogos/stanford.webp", score: 770, type: "reading", isTop: false },
  { name: "MIT", logo: "/images/collegelogos/mit.svg", score: 790, type: "math", isTop: true },
  { name: "Duke", logo: "/images/collegelogos/duke.webp", score: 750, type: "math", isTop: true },
  //{ name: "UCLA", logo: "/images/collegelogos/ucla.webp", score: 730, type: "math", isTop: false },
  { name: "UMich", logo: "/images/collegelogos/michigan.jpg", score: 720, type: "reading", isTop: false },
  { name: "UNC", logo: "/images/collegelogos/unc.webp", score: 690, type: "math", isTop: true },
  { name: "UT Austin", logo: "/images/collegelogos/austin.jpg", score: 680, type: "reading", isTop: false },
  { name: "Clemson", logo: "/images/collegelogos/clemson.png", score: 660, type: "reading", isTop: true },
  //{ name: "Florida State", logo: "/images/collegelogos/floridastate.webp", score: 650, type: "reading", isTop: false },
  //{ name: "Arizona State", logo: "/images/collegelogos/asu.jpg", score: 620, type: "math", isTop: false },
  { name: "LSU", logo: "/images/collegelogos/lsu.jpg", score: 610, type: "reading", isTop: false },
  { name: "U of SC", logo: "/images/collegelogos/uofsc.webp", score: 640, type: "math", isTop: true },
  { name: "Ohio State", logo: "/images/collegelogos/ohio.webp", score: 590, type: "math", isTop: true },
]

const californiaUniversities = [
    // UC Schools (using 75th percentile scores for competitive comparison)
    { name: "UC Berkeley", logo: "/images/collegelogos/ucberkeley.png", score: 780, type: "math", isTop: true },
    { name: "Stanford", logo: "/images/collegelogos/stanford.webp", score: 770, type: "reading", isTop: false },
    { name: "UC Santa Cruz", logo: "/images/collegelogos/ucsantacruz.png", score: 680, type: "reading", isTop: false },
    { name: "UC Santa Barbara", logo: "/images/collegelogos/ucsantabarbara.png", score: 730, type: "math", isTop: true },
    { name: "UC Davis", logo: "/images/collegelogos/ucdavis.jpg", score: 730, type: "reading", isTop: false },
    { name: "UC Merced", logo: "/images/collegelogos/ucmerced.jpg", score: 620, type: "math", isTop: false },
    
    // CSU Schools (using average scores)
    { name: "Cal Poly SLO", logo: "/images/collegelogos/calpoly.png", score: 650, type: "math", isTop: true },
    { name: "San José State", logo: "/images/collegelogos/sanjosestate.png", score: 570, type: "reading", isTop: false },
    { name: "CSU Monterey Bay", logo: "/images/collegelogos/csumb.png", score: 520, type: "math", isTop: true },
    { name: "CSU East Bay", logo: "/images/collegelogos/csueastbay.png", score: 510, type: "reading", isTop: false },
    { name: "CSU Fresno", logo: "/images/collegelogos/csufresno.png", score: 500, type: "math", isTop: false },
  
]

const ProgressBar = styled(Box)(({ theme }) => ({
    height: 64,
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    overflow: "hidden",
    border: `1px solid ${theme.palette.grey[300]}`,
  }))

const ScoreMarker = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: 0,
  bottom: 0,
  width: 2,
}))

const ScoreLabel = styled(Typography)(({ theme }) => ({
  position: "absolute",
  transform: "translateX(-50%)",
  whiteSpace: "nowrap",
  fontSize: theme.typography.pxToRem(12),
  padding: theme.spacing(0.5, 1),
  borderRadius: theme.shape.borderRadius,
}))

function ScoreBar({ label, score, startingScore, targetScore, percentiles, universities, type, selectedColleges, activePercentiles }: ScoreBarProps) {
  const theme = useTheme()
  const studentScores = useStudentScores();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  const currentScore = type === "math" ? studentScores.math.current : studentScores.reading.current;

  const toPosition = (value: number | null) => {
    if (value === null || value < MIN_SCORE) return 0;
    if (value > MAX_SCORE) return 100;
    return ((value - MIN_SCORE) / (MAX_SCORE - MIN_SCORE)) * 100;
  }

  const renderSelectedCollegesInfo = () => {
    if (selectedColleges.length === 0) return null;

    return selectedColleges.map((college, index) => {
      const collegeScores = type === 'math' ? college.mathScores : college.readingWritingScores;
      const activeScoreKeys = (Object.keys(activePercentiles) as Array<keyof ActivePercentiles>)
        .filter(key => activePercentiles[key]);
      
      const validScores = activeScoreKeys
        .map(key => collegeScores[key])
        .filter(s => s !== null && s >= MIN_SCORE && s <= MAX_SCORE) as number[];

      if (validScores.length === 0) return null;

      // Calculate vertical offset - bars are 12px high with 2px spacing (14px total per bar)
      const barHeight = 12;
      const spacing = 2;
      const totalBarSpace = barHeight + spacing;
      let verticalOffset: number;
      
      // Stack colleges: first 4 inside the bar, then alternate above and below
      if (index < 4) {
        // Inside the main progress bar - position from bottom up
        // Main bar is 64px high, so center is at 32px
        // Start from bottom (32px below center) and stack up
        verticalOffset = 32 - (barHeight / 2) - (index * totalBarSpace);
      } else {
        // Outside the bar - alternate above and below
        const outsideIndex = index - 4;
        const isAbove = outsideIndex % 2 === 0;
        const stackLevel = Math.floor(outsideIndex / 2) + 1;
        
        if (isAbove) {
          // Above the main bar
          verticalOffset = -32 - (barHeight / 2) - ((stackLevel - 1) * totalBarSpace);
        } else {
          // Below the main bar
          verticalOffset = 32 + (barHeight / 2) + ((stackLevel - 1) * totalBarSpace);
        }
      }
      
      const colors = [
        theme.palette.info.main,
        theme.palette.secondary.main,
        theme.palette.warning.main,
        theme.palette.success.main,
        theme.palette.error.main,
        '#9C27B0', // Purple
        '#FF5722', // Deep Orange
        '#795548'  // Brown
      ];
      const collegeColor = colors[index % colors.length];

      if (validScores.length === 1) {
        const scoreValue = validScores[0];
        const percentileKey = activeScoreKeys.find(key => collegeScores[key] === scoreValue);
        const position = toPosition(scoreValue);
        
        // For single scores, show as a bar with same height as multi-score bars
        const barWidth = 4; // Small width for single score bars
        
        return (
          <Box
            key={`${college.name}-${index}`}
            title={`${college.name} (${percentileKey}: ${scoreValue})`}
            sx={{
              position: 'absolute',
              left: `calc(${position}% - ${barWidth/2}px)`, // Center the bar on the score
              width: `${barWidth}px`,
              top: `calc(50% + ${verticalOffset}px)`, 
              height: `${barHeight}px`,
              backgroundColor: collegeColor,
              opacity: 0.8,
              transform: 'translateY(-50%)',
              zIndex: 2 + index,
              borderRadius: '2px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'visible',
            }}
          >
            <Typography 
              variant="caption" 
              sx={{ 
                position: 'absolute',
                left: '50%',
                top: '-20px',
                transform: 'translateX(-50%)',
                backgroundColor: collegeColor,
                color: theme.palette.getContrastText(collegeColor),
                border: `1px solid ${collegeColor}`,
                padding: '2px 6px',
                borderRadius: '4px',
                fontSize: theme.typography.pxToRem(9),
                whiteSpace: 'nowrap',
                zIndex: 10,
              }}
            >
              {college.name} ({percentileKey})
            </Typography>
          </Box>
        );
      }

      if (validScores.length > 1) {
        const minCollegeScore = Math.min(...validScores);
        const maxCollegeScore = Math.max(...validScores);
        const startPosition = toPosition(minCollegeScore);
        const width = toPosition(maxCollegeScore) - startPosition;

        if (width <= 0) return null;

        return (
          <Box
            key={`${college.name}-${index}`}
            title={`${college.name} (${minCollegeScore}-${maxCollegeScore})`}
            sx={{
              position: 'absolute',
              left: `${startPosition}%`,
              width: `${width}%`,
              top: `calc(50% + ${verticalOffset}px)`, 
              height: `${barHeight}px`,
              backgroundColor: collegeColor,
              opacity: 0.8,
              transform: 'translateY(-50%)',
              zIndex: 2 + index,
              borderRadius: '3px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}
          >
            <Typography 
              variant="caption" 
              sx={{ 
                color: theme.palette.getContrastText(collegeColor), 
                fontWeight: 'bold', 
                whiteSpace: 'nowrap', 
                overflow: 'hidden', 
                textOverflow: 'ellipsis', 
                px: 0.5, 
                fontSize: theme.typography.pxToRem(9)
              }}
            >
              {college.name}
            </Typography>
          </Box>
        );
      }
      return null;
    });
  };

  return (
    <Box sx={{ mb: 8 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <Typography variant="h6" color="primary.main">{label}</Typography>
      </Box>

      <Box sx={{ position: "relative" }}>
        <ProgressBar>
          <Box
            sx={{
              height: "100%",
              width: `${toPosition(currentScore)}%`,
              backgroundColor: currentScore ? theme.palette.primary.dark : theme.palette.grey[300],
              transition: theme.transitions.create("width", { duration: theme.transitions.duration.enteringScreen }),
            }}
          />
        </ProgressBar>
        
        {/* Render selected colleges information overlayed on the ProgressBar */}
        {renderSelectedCollegesInfo()}

        {/* Markers for national percentiles, target score, etc. in a layer above/below progress bar */}
        <Box sx={{ position: "absolute", inset: 0, overflow: "visible" }}>
          {targetScore !== null && (
            <ScoreMarker sx={{ left: `${toPosition(targetScore)}%`, backgroundColor: theme.palette.success.main, zIndex: 1 }}>
              <ScoreLabel
                sx={{
                  transform: "translateX(-50%) translateY(60px)", // Position below bar
                  backgroundColor: theme.palette.secondary.dark,
                  color: theme.palette.getContrastText(theme.palette.secondary.dark),
                  padding: "6px 8px",
                }}
              >
                Target: {targetScore}
              </ScoreLabel>
            </ScoreMarker>
          )}

          {!isMobile && Object.entries(percentiles).map(([percentile, value]) => {
            // Define scale values to avoid conflicts
            const scaleValues = [MIN_SCORE, 400, 500, 600, 700, MAX_SCORE];
            const hasConflict = scaleValues.includes(value);
            
            return (
              <ScoreMarker
                key={percentile}
                sx={{ left: `${toPosition(value)}%`, backgroundColor: theme.palette.text.disabled, zIndex: 1 }}
              >
                <ScoreLabel sx={{ bottom: "100%", mb: 1, color: theme.palette.text.secondary }}>
                  {`${percentile}th percentile`}
                </ScoreLabel>
                {!hasConflict && (
                  <ScoreLabel sx={{ top: "100%", mt: 1, color: theme.palette.text.secondary }}>
                    {value}
                  </ScoreLabel>
                )}
              </ScoreMarker>
            );
          })}

          {/* Commented out existing universities display - may revisit later
          {!isMobile && universities
            .filter((uni) => uni.type === type)
            .map((uni) => {
              const position = toPosition(uni.score);
              const getTopPosition = () => {
                if (uni.isTop) return -86; 
                return "120%";
              };
              return (
                <Box
                  key={uni.name}
                  sx={{
                    position: "absolute",
                    left: `${position}%`,
                    top: getTopPosition(),
                    transform: "translateX(-50%)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    zIndex: 0,
                  }}
                >
                  <Box component="img" src={uni.logo} alt={uni.name} sx={{ width: 32, height: 32, mb:0.5 }} /> 
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Typography variant="caption" sx={{fontSize: '0.7rem'}}>{uni.name}</Typography>
                    <Typography variant="caption" sx={{ fontWeight: "bold", fontSize: '0.7rem' }}>
                      ({uni.score})
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      position: "absolute",
                      top: uni.isTop ? "calc(100% + 4px)" : "auto",
                      bottom: uni.isTop ? "auto" : "calc(100% + 4px)", // Position line below logo box if not isTop
                      left: "50%",
                      width: 1,
                      height: uni.isTop ? 60 : 40, // Adjust line length
                      borderLeft: `1px dashed ${theme.palette.text.disabled}`,
                    }}
                  />
                </Box>
              );
            })} */}
        </Box>

        {/* Score scale numbers */}
        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5, position: "relative", px: 1 }}>
          {[isMobile ? 300 : MIN_SCORE, 400, 500, 600, 700, MAX_SCORE].map((value) => (
            <Typography 
              key={value}
              variant="caption" 
              color="text.secondary"
              sx={{
                position: "absolute",
                left: `${toPosition(value)}%`,
                transform: "translateX(-50%)",
                top: '8px' // Position below the progress bar
              }}
            >
              {value}
            </Typography>
          ))}
        </Box>

        {currentScore !== null && (
          <Typography variant="caption" 
            sx={{
              position: "absolute", 
              left: `${toPosition(currentScore)}%`, 
              top: "-20px", // Position above the progress bar
              transform: "translateX(-50%)",
              color: theme.palette.getContrastText(theme.palette.primary.dark),
              fontWeight: "bold",
              backgroundColor: theme.palette.primary.dark,
              padding: "4px 6px",
              borderRadius: "4px",
              zIndex: 1,
            }}
          >
            Current: {currentScore}
          </Typography>
        )}
      </Box>
    </Box>
  )
}

export default function ScoreProgressComparison() {
  const studentScores = useStudentScores();
  const theme = useTheme()
  const { user, updateUserData } = useContext(UserContext);
  const [isEditing, setIsEditing] = useState(false);

  // Get selected colleges from user data or initialize as empty array
  const selectedColleges = useMemo(() => {
    return user?.selectedColleges || [];
  }, [user?.selectedColleges]);

  const [activePercentiles, setActivePercentiles] = useState<ActivePercentiles>({
    '25th': true,
    '50th': false, // Default to showing the 25th to 75th percentile range
    '75th': true,
  });

  // Define colors to match the bars
  const chipColors = [
    theme.palette.info.main,
    theme.palette.secondary.main,
    theme.palette.warning.main,
    theme.palette.success.main,
    theme.palette.error.main,
    '#9C27B0', // Purple
    '#FF5722', // Deep Orange
    '#795548'  // Brown
  ];

  const handleAddCollege = useCallback(async (college: CollegeSearchResult) => {
    if (selectedColleges.length >= 8) {
      console.warn("Maximum of 8 colleges can be selected");
      return;
    }
    
    // Check if college is already selected
    const isAlreadySelected = selectedColleges.some(c => c.name === college.name);
    if (isAlreadySelected) {
      console.warn("College is already selected");
      return;
    }

    const updatedColleges = [...selectedColleges, college];
    await updateUserData({ selectedColleges: updatedColleges });
  }, [selectedColleges, updateUserData]);

  const handleRemoveCollege = useCallback(async (collegeName: string) => {
    const updatedColleges = selectedColleges.filter(c => c.name !== collegeName);
    await updateUserData({ selectedColleges: updatedColleges });
  }, [selectedColleges, updateUserData]);

  const handlePercentileChange = (percentiles: ActivePercentiles) => {
    setActivePercentiles(percentiles);
  };

  const cardSubheader = useMemo(() => {
    if (studentScores.math.starting !== null && studentScores.reading.starting !== null &&
        studentScores.math.current !== null && studentScores.reading.current !== null &&
        studentScores.math.current > studentScores.math.starting && 
        studentScores.reading.current > studentScores.reading.starting) {
      return `${user?.firstName || 'Your'} progress: Math has increased by ${studentScores.math.current - studentScores.math.starting} points and Reading by ${studentScores.reading.current - studentScores.reading.starting} points since starting! Keep it up!`;
    }    
    return "Track your progress and see how you stack up against your target schools!";
  }, [studentScores, user]);

  return (
    <Card elevation={1} sx={{ width: '100%', overflow: "visible" }}>
      <CardHeader 
        title="Score Progress & College Comparison" 
        subheader={cardSubheader}
        titleTypographyProps={{ align: 'center' }}
        subheaderTypographyProps={{ align: 'center', variant: 'body2', color: 'text.secondary', sx: { mx: 'auto', maxWidth: '80%'} }}
      />
      <CardContent sx={{ px: { xs: 1, sm: 2}, pt: 2, pb: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: { xs: 'stretch', md: 'center' }, mb: 2 }}>
          <CollegeScoreSearch 
            onCollegeSelect={handleAddCollege}
            onPercentileChange={handlePercentileChange}
            initialPercentiles={activePercentiles} 
            maxColleges={8}
            selectedCount={selectedColleges.length}
          />
          
          {selectedColleges.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, flex: 1 }}>
              {selectedColleges.map((college, index) => {
                const chipColor = chipColors[index % chipColors.length];
                return (
                  <Chip
                    key={college.name}
                    label={college.name}
                    onDelete={() => handleRemoveCollege(college.name)}
                    deleteIcon={<CloseIcon />}
                    variant="outlined"
                    size="small"
                    sx={{ 
                      backgroundColor: chipColor,
                      color: theme.palette.getContrastText(chipColor),
                      borderColor: chipColor,
                      '& .MuiChip-deleteIcon': {
                        fontSize: '18px',
                        color: theme.palette.getContrastText(chipColor),
                      }
                    }}
                  />
                );
              })}
            </Box>
          )}
        </Box>
        
        <ScoreBar
          label="Math"
          score={studentScores.math.current}
          startingScore={studentScores.math.starting}
          targetScore={studentScores.math.target}
          percentiles={studentScores.math.percentiles}
          universities={universities}
          type="math"
          selectedColleges={selectedColleges}
          activePercentiles={activePercentiles}
        />
        <ScoreBar
          label="Reading & Writing"
          score={studentScores.reading.current}
          startingScore={studentScores.reading.starting}
          targetScore={studentScores.reading.target}
          percentiles={studentScores.reading.percentiles}
          universities={universities}
          type="reading"
          selectedColleges={selectedColleges}
          activePercentiles={activePercentiles}
        />

        <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: 'center', gap: 2, mt: 4 }}>
          {[
            studentScores.math.starting !== null && { color: theme.palette.primary.dark, label: "Your Starting Score"},
            { color: theme.palette.text.disabled, label: "National Percentiles" },
            studentScores.math.target !== null && { color: theme.palette.secondary.dark, label: "Your Target Score" },
            //selectedColleges.length > 0 && (activePercentiles['25th'] || activePercentiles['50th'] || activePercentiles['75th']) && 
              //{ color: theme.palette.info.main, label: `Selected Colleges Score Ranges` }
          ]
          .filter((item): item is { color: string; label: string } => Boolean(item)) 
          .map((item, index) => (
            <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: item.color }} />
              <Typography variant="caption" color="text.secondary">{item.label}</Typography>
            </Box>
          ))}
        </Box>

      </CardContent>
    </Card>
  )
}

